import { useCallback, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import useExplainEntityWithAiCall, {
  analysisAiScreenshotDivElementId,
  dashboardAiScreenshotDivElementId,
} from './useExplainEntityWithAiCall';
import {
  ILlmSummarizeResponseFieldsFragment,
  ILlmSummarizeSectionsInput,
  LlmSubjectKind,
} from '../../utils/apollo/graphqlSchema';
import { MaybeNil } from '../../utils/interfaces';
import {
  llmPopulateSubjectSummary,
  llmSetLoading,
  llmToggleSummarySection,
} from '../../redux/slices/llm';
import { UnreachableCaseError } from 'ts-essentials';
import {
  defaultLlmSubjectState,
  getLlmCache,
} from '../../redux/slices/llm/utils';
import { RootState } from '../../redux/store';

interface IProps {
  subjectId: MaybeNil<string>;
  subjectKind: LlmSubjectKind;
  takeScreenshot: boolean;
  // must be set to the analysis schema for analysies and null for dashboards
  schemaId: string | null;
}

export type LlmSummarySectionKey = keyof ILlmSummarizeSectionsInput;

interface IExplainEntityWithAiModel {
  loading: boolean;
  text: string | null;
  richText: string | null;
  sections: ILlmSummarizeSectionsInput;
  toggleSection: (section: LlmSummarySectionKey) => void;
  explain: (prompt: MaybeNil<string>) => void;
  updatedAt: string | null;
}

export const getLlmState = (
  state: RootState,
  subjectKind: LlmSubjectKind,
  subjectId: MaybeNil<string>,
) => {
  if (!subjectId) {
    return defaultLlmSubjectState;
  }
  const cache = getLlmCache(state.llm, subjectKind);
  return cache[subjectId] ?? defaultLlmSubjectState;
};

const useExplainEntityWithAiModel = ({
  subjectId,
  subjectKind,
  takeScreenshot,
  schemaId,
}: IProps): IExplainEntityWithAiModel => {
  const dispatch = useAppDispatch();

  const llmState = useAppSelector((rootState: RootState) =>
    getLlmState(rootState, subjectKind, subjectId),
  );

  const sections = llmState.sections;
  const toggleSection = useCallback(
    (section: LlmSummarySectionKey) => {
      if (subjectId) {
        dispatch(
          llmToggleSummarySection({
            subjectKind,
            subjectId,
            section,
          }),
        );
      }
    },
    [subjectId, subjectKind, dispatch],
  );

  const onExplainComplete = useCallback(
    (resp: ILlmSummarizeResponseFieldsFragment) => {
      if (subjectId && resp.summary) {
        dispatch(
          llmPopulateSubjectSummary({
            subjectKind,
            subjectId,
            summary: resp.summary,
          }),
        );
      }
    },
    [subjectId, subjectKind, dispatch],
  );

  const setLoading = useCallback(
    (loading: boolean) => {
      if (subjectId) {
        dispatch(
          llmSetLoading({
            subjectKind,
            subjectId,
            loading,
          }),
        );
      }
    },
    [subjectId, subjectKind, dispatch],
  );

  const divElementId = useMemo(() => {
    let elementId: string | null = null;
    switch (subjectKind) {
      case LlmSubjectKind.Analysis:
        elementId = analysisAiScreenshotDivElementId(subjectId ?? '');
        break;
      case LlmSubjectKind.Dashboard:
        elementId = dashboardAiScreenshotDivElementId(subjectId ?? '');
        break;
      default:
        throw new UnreachableCaseError(subjectKind);
    }
    return elementId;
  }, [subjectId, subjectKind]);

  const explainWithAiCall = useExplainEntityWithAiCall({
    loading: llmState.loading,
    onExplainComplete,
    setLoading,
    divElementId,
    subjectId,
    subjectKind,
    schemaId,
    sections,
  });

  const explain = useCallback(
    (prompt: MaybeNil<string>) => {
      explainWithAiCall({
        prompt,
        clientRawData: llmState.cachedRawData,
        takeScreenshot,
      });
    },
    [explainWithAiCall, llmState.cachedRawData, takeScreenshot],
  );

  return useMemo(
    () => ({
      loading: llmState.loading,
      explain,
      sections,
      toggleSection,
      text: llmState.llmSummary?.text ?? null,
      richText: llmState.llmSummary?.richText ?? null,
      updatedAt: llmState.llmSummary?.updatedAt ?? null,
    }),
    [
      explain,
      sections,
      llmState.llmSummary?.text,
      llmState.llmSummary?.richText,
      llmState.llmSummary?.updatedAt,
      llmState.loading,
      toggleSection,
    ],
  );
};

export default useExplainEntityWithAiModel;
